// Authentication service
import { LoginRequest } from "../types/LoginRequest";
import { mockLogin, mockLogout, getMockToken } from "./mockData";
import {
	apiClient,
	getAuthTokenFromCookieHelper,
	TOKEN_COOKIE_NAME_EXPORT,
} from "./apiClient";

// Check if we should use mock API
const USE_MOCK_API =
	process.env.REACT_APP_USE_MOCK_API === "true" ||
	!process.env.REACT_APP_API_BASE_URL;

/**
 * Login user
 */
export const login = async (credentials: LoginRequest): Promise<void> => {
	if (USE_MOCK_API) {
		return mockLogin(credentials);
	}

	const response = await apiClient.post("/auth/login", credentials);

	// Note: Browsers don't allow JavaScript to read Set-Cookie headers for security reasons
	// Even though you see it in Network tab, response.headers["set-cookie"] will be undefined
	// However, if Set-Cookie header is present, the browser automatically stores the cookie

	console.log("🔍 Login Response Status:", response.status);
	console.log("📋 Available response headers:", Object.keys(response.headers));

	// Wait a moment for browser to process the cookie (browser sets it automatically from Set-Cookie header)
	await new Promise((resolve) => setTimeout(resolve, 100));

	// Note: If cookie is HttpOnly, we cannot read it via document.cookie (this is by design for security)
	// The browser automatically sends HttpOnly cookies with requests when withCredentials: true
	// So even if we can't read it here, it will still be sent to the backend on subsequent requests

	// Try to check if cookie is readable (will be null if HttpOnly)
	const cookie = getAuthTokenFromCookieHelper();
	if (cookie) {
		console.log(
			"✅ Cookie is readable (not HttpOnly):",
			cookie.substring(0, 20) + "...",
		);
		console.log(`✅ Cookie name: ${TOKEN_COOKIE_NAME_EXPORT}`);
	} else {
		console.log(
			"ℹ️ Cookie cannot be read via JavaScript (likely HttpOnly - this is normal and secure)",
			"\nThe browser will still automatically send the cookie to the backend on subsequent requests.",
			"\n\nTo verify cookie was set:",
			"\n  1. Open DevTools → Application tab → Cookies",
			"\n  2. Check if cookie exists (may be under the backend domain)",
			"\n  3. If cookie is HttpOnly, it won't be readable but will still be sent with requests",
		);
	}

	return Promise.resolve();
};

/**
 * Verify if user is authenticated by checking with backend
 * Returns true if authenticated, false otherwise
 */
export const verifyAuth = async (): Promise<boolean> => {
	if (USE_MOCK_API) {
		// For mock, check localStorage
		const token = getMockToken();
		return !!token;
	}

	try {
		// Verify authentication with backend
		await apiClient.get("/auth/me");
		return true;
	} catch (error: any) {
		// 401 means not authenticated
		if (error.response?.status === 401) {
			return false;
		}
		// Other errors might be network issues, but we'll treat as not authenticated
		console.warn("Auth verification failed:", error);
		return false;
	}
};

/**
 * Logout user
 */
export const logout = async (): Promise<void> => {
	if (USE_MOCK_API) {
		return mockLogout();
	}

	await apiClient.post("/auth/logout");
	return Promise.resolve();
};

